import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useSetState } from 'ahooks'
import { SGeneralForms } from 'shj-components'
import { Button, Input, message, Radio, Select, Spin, TreeSelect } from 'antd'
import { getProductEnums, getProjectPage } from '@/services/product.ts'
import { debounce, isEmpty } from 'lodash-es'
import { replaceRule, trimStr } from '@/utils/data.ts'
import DebounceSearchSelect from '@/pages/baseProduct/components/DebounceSearchSelect'
import RulesModal from '@/pages/baseProduct/components/RuleModal/RulesModal.tsx'
import { getChildrenDicts } from '@/services'
import _ from 'lodash'

type IProps = {
  fn: () => void
  readOnly?: boolean
  options: Record<string, any[]>
  companyList: any[]
  record: any
}

export const AddOrEditProject = forwardRef((props: IProps, ref) => {
  const { companyList } = props

  const formRef = useRef()
  const [state, setState] = useSetState<any>({
    subChooseValue: [],
    detailInfo: {},
    loading: false,
  })
  const [optionList, setOptionList] = useState([{ id: '0', name: '通用' }, ...companyList])

  const [ruleModal, setRuleModal] = useSetState({
    open: false,
    mode: 'modal',
    value: '',
  })

  const onFormRef = () => {
    const { onFormRef } = formRef.current || {}
    return onFormRef?.()
  }

  useEffect(() => {
    onFormRef()?.resetFields()
    setRuleModal({ value: '' })
    if (!isEmpty(props.record)) {
      const item = props.record
      const shops = item?.shops || []
      const rule = JSON.parse(item.subFilterRule || null)?.rule || ''
      const obj = {
        ...item,
        shops: shops,
        shopNames: item.shopList?.map(item => item.companyName),
      }
      onFormRef()?.setFieldsValue(obj)
      setState({ detailInfo: obj })
      setRuleModal({ value: rule })
    }
    if (!props.record?.subChooseType && state.subChooseValue.length) {
      onFormRef()?.setFieldValue('subChooseType', state.subChooseValue[0]?.value)
    }
    shopsControl(props.record?.shops || ['0'])
  }, [props.record, state.subChooseValue])

  const getOptions = async (): Promise<{ [key: string]: any }> => {
    setState({ loading: true })
    const subChooseValue = await getProductEnums(
      'com.shj.saas.plus.product.enums.GenChooseConfineEnum'
    )
    const space = await getChildrenDicts(['spaceKey'])
    setState({
      subChooseValue: subChooseValue.map(item => ({ label: item.description, value: item.key })),
      loading: false,
    })
    return Promise.resolve({ ...props.options, spaceList: space[0]?.children })
  }

  const getShopArr = (codeArr: string[] = []) => {
    return optionList?.reduce((res, item) => {
      if (codeArr.includes(item.id)) {
        res.push({ id: item.id, companyName: item.name })
      }
      return res
    }, [])
  }
  const shopsControl = (value: any) => {
    let newOption: any = _.cloneDeep(optionList)
    if (value?.length) {
      if (value.includes('0')) {
        newOption = newOption.map((item: any) => {
          return {
            ...item,
            disabled: item.id !== '0',
          }
        })
      } else {
        newOption = newOption.map((item: any) => {
          return {
            ...item,
            disabled: item.id === '0',
          }
        })
      }
    } else {
      newOption = newOption.map((item: any) => {
        return {
          ...item,
          disabled: false,
        }
      })
    }
    setOptionList(newOption)
  }
  const onFinish = () => {
    return new Promise((resolve, reject) => {
      return onFormRef()
        ?.validateFields()
        .then(values => {
          console.log(values, 'values')
          if (values.shops?.includes('0') && values.shops?.length > 1) {
            message.warning('通用不能和其他门店同时选择')
            reject()
            return
          }
          props.setLoading?.(true)
          const shopList = values.shops ? getShopArr(values.shops) : []
          const newValues = {
            id: values.id,
            busId: values.busId,
            dtlName: values.dtlName,
            busType: 'PRD_PROJECT',
            busTypeName: '项目',
            spaceTag: values.spaceTag,
            isMust: values.isMust,
            subChooseType: values.subChooseType,
            isCopy: values.isCopy,
            isShowOrder: values.isShowOrder,
            subChooseValue: values.subChooseValue,
            isShowSum: values.isShowSum,
            shopList: shopList,
            shops: values.shops,
            subFilterRule: JSON.stringify({ rule: ruleModal.value }),
            children: props.record.children,
          }
          resolve(newValues)
        })
    })
  }

  useImperativeHandle(ref, () => {
    return {
      onFinish,
    }
  }, [onFinish])

  return (
    <>
      <Spin spinning={state.loading}>
        <SGeneralForms
          isDetail={props.readOnly}
          ref={formRef}
          isHideGobackBtn={true}
          httpOptionsFn={getOptions}
          initialValues={
            !props.record.id
              ? {
                  isMust: true,
                  isCopy: true,
                  isShowOrder: true,
                  isShowSum: true,
                  shops: ['0'],
                }
              : {}
          }
          layout="vertical"
          titleMode={props.readOnly ? '' : 'edit'}
          configs={[
            {
              title: '基础信息',
              formItem: [
                { name: 'id', hidden: true },
                {
                  name: 'busId',
                  label: '项目',
                  required: true,
                  rules: [{ required: true, message: '项目不能为空' }],
                  spanCol: 24,
                  children: (
                    <DebounceSearchSelect
                      nameKey={'itemNameLike'}
                      readOnly={props.readOnly}
                      label={'项目'}
                      query={{ id: props.record.busId }}
                      fetchOptions={getProjectPage}
                      fieldNames={{ label: 'itemName' }}
                      onChange={(v, o) => o && onFormRef()?.setFieldValue('dtlName', o.label)}
                    />
                  ),
                },
                {
                  name: 'dtlName',
                  label: '显示名称',
                  required: true,
                  rules: [{ required: true, message: '显示名称不能为空' }],
                  spanCol: 24,
                  comConfig: {
                    showCount: true,
                    maxLength: 15,
                    onChange: e => onFormRef()?.setFieldValue('dtlName', trimStr(e.target.value)),
                  },
                },
              ],
            },
            {
              title: '打包配置',
              titleMode: 'drawer',
              formItem: [
                {
                  name: 'isMust',
                  label: '是否必选',
                  spanCol: 24,
                  type: 'switch',
                  comConfig: {
                    checkedChildren: '是',
                    unCheckedChildren: '否',
                  },
                },
                {
                  name: 'subChooseType',
                  label: '子集选择限制',
                  spanCol: 24,
                  children: props.readOnly ? (
                    <div>
                      {state.subChooseValue.find(
                        item => item.value === state.detailInfo.subChooseType
                      )?.label || '全部'}
                    </div>
                  ) : (
                    <Radio.Group options={state.subChooseValue} />
                  ),
                },
                {
                  name: 'subChooseValue',
                  label: '多选个数上限',
                  isDeps: true,
                  deps: form => form.getFieldValue('subChooseType') === 'MORE_CHOOSE',
                  spanCol: 24,
                  required: true,
                  rules: [{ required: true, message: '多选个数上限不能为空' }],
                  type: 'inputNumber',
                  comConfig: { min: 2, precision: 0, max: 9999 },
                },
                {
                  label: '子集产品过滤',
                  spanCol: 24,
                  [props.readOnly ? 'name' : '']: 'subFilterRule',
                  [props.readOnly ? '' : 'children']: (
                    <div>
                      <Button onClick={() => setRuleModal({ open: true })}>编辑规则</Button>
                      {ruleModal?.value && (
                        <Input.TextArea
                          autoSize={{ minRows: 1, maxRows: 4 }}
                          disabled
                          style={{ marginTop: 6 }}
                          value={replaceRule(ruleModal.value)}
                        />
                      )}
                    </div>
                  ),
                },
                {
                  name: 'spaceTag',
                  label: '空间标签',
                  type: 'select',
                  data: 'spaceList',
                  comConfig: {
                    fieldNames: { value: 'priKey', label: 'name' },
                  },
                  spanCol: 24,
                },
                {
                  name: 'isCopy',
                  label: '是否允许复制',
                  spanCol: 24,
                  type: 'switch',
                  comConfig: {
                    checkedChildren: '是',
                    unCheckedChildren: '否',
                  },
                },
              ],
            },
            {
              title: '显示配置',
              titleMode: 'drawer',
              formItem: [
                {
                  name: 'isShowOrder',
                  label: '下单时是否显示',
                  spanCol: 24,
                  type: 'switch',
                  comConfig: {
                    checkedChildren: '是',
                    unCheckedChildren: '否',
                  },
                },
                {
                  name: 'isShowSum',
                  label: '汇总金额是否显示',
                  spanCol: 24,
                  type: 'switch',
                  comConfig: {
                    checkedChildren: '是',
                    unCheckedChildren: '否',
                  },
                },
              ],
            },
            {
              title: '适用渠道',
              titleMode: 'drawer',
              formItem: [
                {
                  name: 'shops',
                  label: '适用门店',
                  spanCol: 24,
                  children: props.readOnly ? (
                    <div>{state.detailInfo?.shopNames}</div>
                  ) : (
                    <Select
                      mode={'multiple'}
                      optionFilterProp={'name'}
                      placeholder={'请选择适用门店'}
                      fieldNames={{ label: 'name', value: 'id' }}
                      onChange={(value, option) => {
                        shopsControl(value)
                      }}
                      options={optionList}
                    />
                  ),
                },
              ],
            },
          ]}
        />
      </Spin>
      <RulesModal
        modalProps={{ ...ruleModal }}
        // type={'subItem'}
        type={'productFilterRule'}
        onCancel={() => setRuleModal({ open: false })}
        onOk={(value: string) => setRuleModal({ value })}
      />
    </>
  )
})
